import React, { useState, useEffect } from 'react';
import { X, Radar, Rocket, RefreshCw, Power, Clock } from 'lucide-react';
import { useRobloky } from '../../context/RoblokyContext';
import { useAuth } from '../../context/AuthContext';
import { formatCompact } from '../../utils/format';

interface ChartGame {
  placeId: number;
  title: string;
  playingCCU?: number;
  totalVisits?: number;
}

export const AdminPanel: React.FC = () => {
  const { setIsAdminPanelOpen, addToast, openGame } = useRobloky();
  const { authFetch } = useAuth();
  const [scoutActive, setScoutActive] = useState(true);
  const [intervalH, setIntervalH] = useState('1');
  const [placeInput, setPlaceInput] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [charts, setCharts] = useState<ChartGame[]>([]);
  const [chartsLoading, setChartsLoading] = useState(false);

  const loadSettings = async () => {
    try {
      const res = await authFetch('/robloky/api/admin/agents');
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.settings?.scout) {
        setScoutActive(data.settings.scout.active !== false);
        setIntervalH(String(Math.round((data.settings.scout.intervalSec || 3600) / 3600)));
      }
    } catch { /* ignore */ }
  };

  const loadCharts = async () => {
    setChartsLoading(true);
    try {
      const res = await authFetch('/robloky/api/scout/roblox-charts');
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setCharts(data.charts || []);
    } catch { /* ignore */ }
    finally {
      setChartsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadCharts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setBusy('settings');
    try {
      const res = await authFetch('/robloky/api/admin/agents', {
        method: 'PUT',
        body: JSON.stringify({
          scout: {
            active: scoutActive,
            intervalSec: Math.min(24, Math.max(1, parseInt(intervalH, 10) || 1)) * 3600,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      addToast({ title: 'Scout schedule saved', message: '', type: 'success' });
    } catch (e: any) {
      addToast({ title: 'Save failed', message: e.message, type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const publish = async (placeIdOrUrl?: string) => {    const key = placeIdOrUrl || 'auto';
    setBusy(key);
    try {
      const res = await authFetch('/robloky/api/scout/publish-roblox', {
        method: 'POST',
        body: JSON.stringify(placeIdOrUrl ? { placeIdOrUrl } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (!data.success && !data.post)) {
        throw new Error(data.error || data.reason || 'Publish failed');
      }
      if (data.success && data.details) {
        addToast({ title: `Published: ${data.details.title}`, message: '', type: 'success' });
        setIsAdminPanelOpen(false);
        openGame(String(data.details.placeId));
      } else {
        addToast({ title: 'Nothing new to publish', message: 'Every chart game is already listed.', type: 'info' });
      }
    } catch (e: any) {
      addToast({ title: 'Publish failed', message: e.message, type: 'error' });
    } finally {
      setBusy(null);
      setPlaceInput('');
    }
  };

  const refreshMedia = async () => {
    setBusy('media');
    try {
      const res = await authFetch('/robloky/api/admin/refresh-media', {
        method: 'POST',
        body: JSON.stringify({ limit: 20 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Refresh failed');
      addToast({
        title: `Media refreshed: ${data.refreshed}/${data.checked}`,
        message: '',
        type: 'success',
      });
    } catch (e: any) {
      addToast({ title: 'Media refresh failed', message: e.message, type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0d111a] p-4 sm:p-6 space-y-5 my-6">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-bold text-white">
            <Radar className="h-5 w-5 text-red-400" />
            Game Scout Console
          </h2>
          <button
            onClick={() => setIsAdminPanelOpen(false)}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <section className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
          <h3 className="flex items-center gap-2 text-xs font-bold font-mono uppercase tracking-wider text-slate-300">
            <Clock className="h-3.5 w-3.5 text-red-400" /> Auto-discovery schedule
          </h3>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setScoutActive((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                scoutActive
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                  : 'bg-slate-800 border-white/10 text-slate-400'
              }`}
            >
              <Power className="h-3.5 w-3.5" />
              {scoutActive ? 'ON' : 'OFF'}
            </button>
            <label className="flex items-center gap-2 text-xs font-mono text-slate-400">
              every
              <input
                value={intervalH}
                onChange={(e) => setIntervalH(e.target.value.replace(/\D/g, '').slice(0, 2))}
                className="w-12 px-2 py-1.5 rounded-lg bg-black/40 border border-white/10 text-center text-white focus:border-red-500 focus:outline-none"
              />
              hour(s)
            </label>
            <button
              onClick={saveSettings}
              disabled={busy === 'settings'}
              className="px-4 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-xs font-bold transition-all disabled:opacity-50"
            >
              {busy === 'settings' ? 'Saving...' : 'Save'}
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
          <h3 className="flex items-center gap-2 text-xs font-bold font-mono uppercase tracking-wider text-slate-300">
            <Rocket className="h-3.5 w-3.5 text-red-400" /> Publish a game
          </h3>
          <div className="flex gap-2">
            <input
              value={placeInput}
              onChange={(e) => setPlaceInput(e.target.value)}
              placeholder="Place ID or Roblox game URL..."
              className="flex-1 px-3.5 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-red-500 focus:outline-none"
            />
            <button
              onClick={() => placeInput.trim() && publish(placeInput.trim())}
              disabled={busy !== null || !placeInput.trim()}
              className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-xs font-bold transition-all disabled:opacity-50 whitespace-nowrap"
            >
              {busy && busy !== 'auto' ? 'Working...' : 'Publish'}
            </button>
          </div>
          <button
            onClick={() => publish()}
            disabled={busy !== null}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-slate-200 text-xs font-bold hover:border-red-500/40 hover:text-red-300 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy === 'auto' ? 'animate-spin' : ''}`} />
            {busy === 'auto' ? 'Discovering...' : 'Auto-discover next chart game'}
          </button>
          <button
            onClick={() => refreshMedia()}
            disabled={busy !== null}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-slate-200 text-xs font-bold hover:border-red-500/40 hover:text-red-300 transition-all disabled:opacity-50"
            title="Re-fetch thumbnails for the 20 stalest games (heals expired CDN URLs)"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy === 'media' ? 'animate-spin' : ''}`} />
            {busy === 'media' ? 'Refreshing media...' : 'Refresh stale game images (20)'}
          </button>
        </section>

        <section className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-2">
          <h3 className="text-xs font-bold font-mono uppercase tracking-wider text-slate-300">
            Live Roblox charts {chartsLoading ? '(loading...)' : `(${charts.length})`}
          </h3>
          <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {charts.map((c) => (
              <li
                key={c.placeId}
                className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-200 truncate">{c.title}</p>
                  <p className="text-[10px] font-mono text-slate-500">
                    {c.playingCCU ? `${formatCompact(c.playingCCU)} playing` : ''} â€¢ {c.placeId}
                  </p>
                </div>
                <button
                  onClick={() => publish(String(c.placeId))}
                  disabled={busy !== null}
                  className="shrink-0 px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-[11px] font-bold hover:bg-red-500/25 transition-all disabled:opacity-50"
                >
                  Publish
                </button>
              </li>
            ))}
            {!chartsLoading && charts.length === 0 && (
              <li className="text-[11px] font-mono text-slate-500">Charts unavailable right now.</li>
            )}
          </ul>
        </section>
      </div>
    </div>
  );
};
